/**
 * EVERY QUESTION THE DESK ASKS ABOUT WORK, ASKED ONCE.
 *
 * Note what is NOT in any of these queries: `account_id`. Row level
 * security answers "which shop" in the database, on every table, for every
 * request. A filter here would be a second answer to a question that
 * already has one, and the day the two disagreed the wrong one would win
 * silently.
 *
 * Money is derived from the line items every time, never read from a column
 * on the job. The old system stored a total AND computed one, and the two
 * drifted. See the note above SELECT for why it is summed here rather than
 * embedded from the job_totals view.
 */

import { db } from "./client.ts";
import { asBillAs } from "../domain/billas.ts";
import { mayCancel, type Role } from "../domain/permissions.ts";
import { isIdentified, words, type Identifiable } from "../domain/trade.ts";
import { shopWords } from "../ui/words.ts";
import { isVisitOnly } from "../domain/visitquote.ts";
import { fromShopWall } from "../ui/shopclock.ts";
import { carSaveRefusal } from "../domain/carsave.ts";

/** The shop's own words, in the shop's own order. */
export const STATUS = ["estimate", "scheduled", "in_progress", "completed", "invoiced", "paid", "cancelled"] as const;
export type Status = (typeof STATUS)[number];

/** What the shop calls each state out loud. The database keeps the noun. */
export const STATUS_LABEL: Record<Status, string> = {
  estimate: "Quote",
  scheduled: "Booked",
  in_progress: "In progress",
  completed: "Done",
  invoiced: "Billed",
  paid: "Paid",
  cancelled: "Cancelled",
};

/**
 * What to call this job's state out loud, given the row.
 *
 * `cancelled` covers two different things: work the shop decided against,
 * and a job cleared off the board. STATUS_LABEL cannot tell them apart
 * because it only sees the status, so anywhere a PERSON reads the state
 * should ask this instead.
 */
export function stateLabel(status: string, archivedAt?: string | null): string {
  if (status === "cancelled" && archivedAt) return "Archived";
  return STATUS_LABEL[status as Status] ?? status;
}

export type Totals = {
  total: number; labour_total: number; parts_total: number;
  /** Repair lines only. What a customer is asked to authorize. See 0119. */
  repair_total: number;
};

export type JobRow = {
  id: string;
  job_number: string;
  /** Set only when this job was quoted at a rate other than the shop's. */
  labour_rate: number | null;
  status: Status;
  title: string | null;
  complaint: string | null;
  /** What the quote says, in the shop's words. The customer reads it. */
  quote_note: string | null;
  /** When the price stops standing. Null until it has been sent. */
  quote_expires_at: string | null;
  /** When the customer came back asking for it to be different. */
  changes_requested_at: string | null;
  /** Why this job is not being billed. Null is an ordinary job. See 0088. */
  no_charge_reason: string | null;
  /** The service advisor looking after this customer. */
  advisor_id: string | null;
  /**
   * Whether anybody has authorized this figure. An aggregate rather than
   * the rows, because the quote list needs to know YES OR NO for five
   * hundred jobs and does not need one of them.
   */
  job_authorizations: Array<{ id: string }> | null;
  notes: string | null;
  /* THE GAUGES, on a job that had air conditioning work on it. Optional
     on the type, not merely nullable, because they arrive through NEWER
     and are absent altogether until 0126 lands. null means not taken and
     0 is a real reading -- see src/domain/acreadings.ts. */
  ac_high_psi?: number | null;
  ac_low_psi?: number | null;
  ac_vent_f?: number | null;
  ac_ambient_f?: number | null;
  service_at: "mobile" | "shop" | null;
  location: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  /**
   * Set when the job was cleared off the board rather than called off.
   * Optional rather than merely nullable, for the same reason as the AC
   * gauges above: it arrives through NEWER and does not exist at all until
   * 0128 lands. Absent reads as Cancelled, which is what it read as before.
   */
  archived_at?: string | null;
  /**
   * THE DIAGNOSTIC CARD, 0188. What the technician found, in their own
   * words, which is what the desk quotes from. Optional for the same reason
   * as archived_at. Read it through diagnosisOf() in data/tests.ts, which
   * falls back to the tagged line in notes for jobs from before the column.
   * The author and time are stamped by a trigger, never sent from here.
   */
  diagnosis?: string | null;
  diagnosed_at?: string | null;
  diagnosed_by?: string | null;
  /** 0189. True keeps the card off the customer's garage page. */
  diagnosis_hidden?: boolean;
  created_at: string;
  customers: {
    id: string; full_name: string; phone: string | null;
    email: string | null; address: string | null; portal_token: string | null;
  } | null;
  vehicles: {
    id: string; year: number | null; make: string | null; model: string | null;
    /** What is under the hood. Two identical-looking cars take different parts. */
    engine: string | null;
    trim: string | null;
    description: string | null;
    // Either one of these closes a job. Both is better.
    license_plate: string | null; vin: string | null; mileage: number | null;
    /**
     * Carried since 0001 and written by the AI agent, but no screen has
     * ever shown it. A garage door shop needs it: a replacement panel has
     * to match what is already on the house. See trade.ts hasColour.
     */
    colour: string | null;
  } | null;
  totals: Totals;
  /**
   * A garage door shop's free estimate visit: the visit line and nothing
   * else, at no charge. The lists print "Free estimate" where the money
   * would go, because "$0.00" on a quote reads as one nobody finished.
   * See src/domain/visitquote.ts.
   */
  visit?: boolean;
};

/**
 * THE MONEY COMES FROM THE LINES, NOT FROM A COLUMN — but not from the
 * `job_totals` view either, and that distinction cost a demo.
 *
 * The view is correct and stays: it is the right thing for SQL and for
 * reporting. It cannot be EMBEDDED from here, though. PostgREST follows
 * foreign keys to embed one resource inside another, and a view that
 * GROUPs BY has no key to follow, so asking for `job_totals(total)` on a
 * job returns "Could not find a relationship between 'jobs' and
 * 'job_totals' in the schema cache".
 *
 * So the lines come back with the job and are added up here — in the data
 * layer, once, where every screen gets the same answer. Still derived,
 * still never stored, which was the whole point of not having a
 * `jobs.total` column.
 *
 * The cost is honest: a hundred jobs brings back their line items too. At
 * a shop's volume that is nothing, and lists are capped at 100 anyway. If
 * that stops being true the fix is a PostgREST computed field — a function
 * taking a `jobs` row — which is selectable by name and costs no extra
 * rows. Not worth the migration today.
 */
const BASE =
  "id,job_number,status,title,complaint,notes,quote_note,quote_expires_at," +
  "labour_rate,scheduled_at,completed_at,created_at," +
  // The token and the email come along because SENDING a quote needs
  // both and the card had no way to reach either.
  "customers(id,full_name,phone,email,address,portal_token)," +
  "vehicles(id,year,make,model,engine,trim,description,license_plate,vin,mileage,colour)," +
  "line_items(kind,total,bill_as,description)," +
  // Did anybody say yes. See quoteStateOf.
  "job_authorizations(id)";

/**
 * THE APP MUST NOT CARE WHICH MIGRATIONS HAVE RUN.
 *
 * I shipped a select for `service_at` before 0003 was applied and every
 * job screen died on "column jobs.service_at does not exist". Deploying
 * code that assumes a migration is a two-part change pretending to be one,
 * and the half that lands first is the half that breaks.
 *
 * So the columns added after 0001 are asked for optimistically and dropped
 * on the one error that means "not yet". It costs a single failed request
 * per session at worst, it heals itself the moment the migration lands,
 * and at no point is there a version of this that shows somebody a crash.
 *
 * This is deliberately narrow: 42703 is undefined_column and nothing else,
 * so a real error still surfaces as a real error.
 */
// `changes_requested_at` is migration 0040 and rides the same optimistic
// path as the rest: asked for, dropped on undefined_column, healed the
// moment the migration lands.
const NEWER = "service_at,location,changes_requested_at,advisor_id,no_charge_reason," +
  // 0126. Listed here rather than in BASE for exactly the reason above:
  // this ships through Railway and the migration ships through the
  // management API, and the two have no common trigger. The job screen
  // renders with no readings section until the columns exist.
  "ac_high_psi,ac_low_psi,ac_vent_f,ac_ambient_f," +
  // 0128, and the same two doors: the column arrives by migration
  // and this file arrives by deploy. Until it lands an archived job
  // simply reads as Cancelled, which is what it read as before.
  "archived_at," +
  // 0188, the diagnostic card. Until it lands the card falls back to the
  // tagged line in notes, which is where the diagnosis lived before.
  "diagnosis,diagnosed_at,diagnosed_by,diagnosis_hidden";
let hasNewer: boolean | null = null;

const selectFor = () => (hasNewer === false ? BASE : `${BASE},${NEWER}`);
const isMissingColumn = (e: { code?: string } | null) => e?.code === "42703";

type RawJob = Omit<JobRow, "totals"> & {
  line_items: Array<{ kind: string; total: number; bill_as?: string | null;
    description?: string | null }> | null };

function withTotals(raw: RawJob): JobRow {
  const lines = raw.line_items ?? [];
  const sum = (pick: (k: string) => boolean): number =>
    lines.filter((l) => pick(l.kind)).reduce((n, l) => n + Number(l.total ?? 0), 0);
  const { line_items: _drop, ...rest } = raw;
  return {
    ...rest,
    visit: isVisitOnly(lines),
    totals: {
      labour_total: sum((k) => k === "labour"),
      parts_total: sum((k) => k === "part"),
      total: sum(() => true),
      /* WHAT A CUSTOMER IS ASKED TO AUTHORIZE. Storage and towing are not
         repairs they agreed to, and a signature against the whole job
         total would be consent for work nobody described. See 0119. */
      repair_total: lines
        .filter((l) => asBillAs(l.bill_as) === "repair")
        .reduce((n, l) => n + Number(l.total ?? 0), 0),
    },
  };
}

/**
 * MOBILE OR SHOP, said the same way everywhere.
 *
 * Null is a real answer — 263 imported jobs have no record of it — and it
 * reads as "not said" rather than being quietly folded into whichever is
 * more common. A shop that is 96% mobile would bury every wrong one.
 */
export const WHERE_LABEL = { mobile: "Mobile", shop: "In the shop" } as const;
export const whereLabel = (s: JobRow["service_at"]): string =>
  s ? WHERE_LABEL[s] : "Not said";

/**
 * The thing a job is about, in one line, whatever it is. Coaches have no
 * model year.
 *
 * THE LABEL FOLLOWS THE TRADE. Anthony, 2026-09-16: "Still says no vehicle
 * in the quotes area." This is the label on every job tile, every quote row
 * and the job card's own header, and it was the last hardcoded "vehicle"
 * left on a screen -- so a garage door shop's quote list read "No vehicle"
 * down the page. shopWords() is primed at boot and reads 'auto' for every
 * shop that has not set a trade, so nothing changed for anybody else.
 */
export function vehicleName(v: JobRow["vehicles"]): string {
  const w = shopWords();
  if (!v) return `No ${w.thing}`;
  const ymm = [v.year, v.make, v.model].filter(Boolean).join(" ").trim();
  return ymm || v.description || w.unidentified;
}

export async function listJobs(
  opts: { statuses?: Status[]; limit?: number; since?: string | null; sinceOn?: string;
          /** A specific set, for a stage that is not "status plus a date". */
          ids?: string[];
          order?: { column: string; ascending: boolean } } = {},
): Promise<JobRow[]> {
  const sb = await db();
  const ord = opts.order ?? { column: "created_at", ascending: false };
  const run = async (sel: string) => {
    let q = sb.from("jobs").select(sel).order(ord.column, { ascending: ord.ascending }).limit(opts.limit ?? 100);
    if (opts.statuses?.length) q = q.in("status", opts.statuses);
    if (opts.ids?.length) q = q.in("id", opts.ids);
    if (opts.since) q = q.gte(opts.sinceOn ?? "created_at", opts.since);
    return q;
  };
  let { data, error } = await run(selectFor());
  if (error && isMissingColumn(error)) {
    hasNewer = false;
    ({ data, error } = await run(BASE));
  } else if (!error) hasNewer = true;
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as RawJob[]).map(withTotals);
}

export async function getJob(id: string): Promise<JobRow | null> {
  const sb = await db();
  let { data, error } = await sb.from("jobs").select(selectFor()).eq("id", id).maybeSingle();
  if (error && isMissingColumn(error)) {
    hasNewer = false;
    ({ data, error } = await sb.from("jobs").select(BASE).eq("id", id).maybeSingle());
  } else if (!error) hasNewer = true;
  if (error) throw new Error(error.message);
  return data ? withTotals(data as unknown as RawJob) : null;
}

export type LineItem = {
  id: string; kind: "labour" | "part" | "fee" | "discount";
  description: string; quantity: number; unit_price: number; total: number;
  /** What the shop paid. Internal — never reaches a customer-facing shape. */
  cost: number | null;
  part_number: string | null; position: number;
  /**
   * CCR 16 section 3356. NULL means nobody has stated it yet, which is
   * NOT the same as new. See src/domain/partcondition.ts.
   */
  part_condition: string | null;
  /**
   * Which document this line prints on: repair, storage or tow. NOT the
   * same question as `kind`, which is a payroll instruction. See
   * src/domain/billas.ts and migration 0118.
   */
  bill_as: string | null;
};

/**
 * WHERE THIS CUSTOMER'S WORK USUALLY HAPPENS.
 *
 * A job saved from the counter carries no location, so the day map falls
 * back to `customers.address` -- and that column is a street with no town
 * on it 1,000 times out of 1,370. Job 1759 is the whole story: Taylor
 * Butler's four earlier jobs all say "25616 Andalucia Drive, Hemet" and the
 * fifth, typed by hand, said nothing, so it dropped off the map as "no town
 * on it" while the desk knew the answer the entire time.
 *
 * So a new job inherits the last location that actually worked. The most
 * RECENT one, not the most common: people move, and the address they gave
 * you last time is the one they are at.
 *
 * Returns null for a customer with no history, which is correct. It is a
 * default, not a guess -- a location that came from this same customer's
 * own previous job is a fact about them, and it is still editable on the
 * job screen like any other.
 */
export async function lastKnownLocation(customerId: string): Promise<string | null> {
  if (!customerId) return null;
  const sb = await db();
  const { data, error } = await sb.from("jobs")
    .select("location")
    .eq("customer_id", customerId)
    .not("location", "is", null)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) return null;
  const hit = (data ?? [])[0] as { location: string | null } | undefined;
  const loc = String(hit?.location ?? "").trim();
  return loc || null;
}

export async function listLines(jobId: string): Promise<LineItem[]> {
  const sb = await db();
  const { data, error } = await sb.from("line_items")
    .select("id,kind,description,quantity,unit_price,total,cost,part_number,part_condition,bill_as,position")
    .eq("job_id", jobId).order("position");
  if (error) throw new Error(error.message);
  return (data ?? []) as LineItem[];
}

export type Authorization = {
  id: string; kind: string; method: string; amount: number;
  authorized_by: string; signature: string | null; declined: boolean;
  authorized_at: string;
  /** repair, storage, tow or teardown. See 0119 and 0127. */
  authorizes: string;
  /* The teardown disclosure: what reassembly costs, what the teardown
     destroys, whether it can go back and how long that takes. Null on
     every other kind of authorization, and 0127 has a check that keeps
     it that way. See src/domain/teardown.ts. */
  teardown_reassembly_cost: number | null;
  teardown_destroyed_cost: number | null;
  teardown_restorable: boolean | null;
  teardown_restore_note: string | null;
  teardown_max_days: number | null;
};

export async function listAuthorizations(jobId: string): Promise<Authorization[]> {
  const sb = await db();
  const { data, error } = await sb.from("job_authorizations")
    .select("id,kind,method,amount,authorized_by,signature,declined,authorized_at,authorizes,teardown_reassembly_cost,teardown_destroyed_cost,teardown_restorable,teardown_restore_note,teardown_max_days")
    .eq("job_id", jobId).order("authorized_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Authorization[];
}

export type JobEvent = {
  id: string; kind: string; actor: string; title: string;
  detail: string | null; created_at: string;
};

export async function listEvents(jobId: string): Promise<JobEvent[]> {
  const sb = await db();
  const { data, error } = await sb.from("job_events")
    .select("id,kind,actor,title,detail,created_at")
    .eq("job_id", jobId).order("created_at", { ascending: false }).limit(50);
  if (error) throw new Error(error.message);
  return (data ?? []) as JobEvent[];
}

/** The numbers on the dashboard, counted in the database rather than fetched and length-ed. */
export async function counts(): Promise<Record<string, number>> {
  const sb = await db();
  const one = async (build: (q: any) => any) => {
    const { count } = await build(sb.from("jobs").select("id", { count: "exact", head: true }));
    return count ?? 0;
  };
  const [quotes, booked, working, unbilled, leads] = await Promise.all([
    one((q: any) => q.eq("status", "estimate")),
    one((q: any) => q.eq("status", "scheduled")),
    one((q: any) => q.eq("status", "in_progress")),
    one((q: any) => q.eq("status", "completed")),
    (async () => {
      const { count } = await sb.from("enquiries").select("id", { count: "exact", head: true })
        .eq("kind", "lead").eq("status", "new");
      return count ?? 0;
    })(),
  ]);
  return { quotes, booked, working, unbilled, leads };
}

/** Same shape as the customer one, same reasoning. */
// quote_note is the paragraph the CUSTOMER reads under the prices, and
// quote_expires_at is when that price stops standing. Both were added in
// 0018 and neither was on this list, so the box saved nothing and said
// nothing about it — an edit that vanishes is worse than a field that is
// missing, because the second one you notice.
const JOB_FIELDS = new Set(["title", "complaint", "notes", "internal_notes", "location",
  "quote_note", "quote_expires_at"]);

/**
 * A field on the CAR, edited from the job it is sitting on.
 *
 * The plate and the VIN are asked for at the counter and read off the car
 * at the end, and the person who ends up filling them in is a technician
 * standing next to it with the job open. Making them navigate to a vehicle
 * record to type six characters is how a gate becomes something people
 * work around instead of with.
 */
/**
 * THE SHOP'S LABOR RATE — read, not assumed.
 *
 * The quote editor had 150 hard-coded, which meant the rate on the
 * settings screen was decoration: changing it changed nothing, and every
 * quote in the system priced labor at a number nobody had chosen. A job
 * may carry its own rate, and where it does that WINS — a job quoted at
 * last year's rate keeps it, because re-pricing agreed work retroactively
 * is how a customer gets a bill that does not match their quote.
 *
 * Cached for the session. It is one number that changes about twice a
 * year, and a round trip per job card for it would be silly.
 */
let cachedRate: number | null = null;

export async function shopLabourRate(): Promise<number> {
  if (cachedRate != null) return cachedRate;
  const sb = await db();
  const { data } = await sb.from("accounts").select("labour_rate").limit(1).maybeSingle();
  const r = Number((data as { labour_rate: number } | null)?.labour_rate);
  cachedRate = Number.isFinite(r) && r > 0 ? r : 150;
  return cachedRate;
}

/** Called when settings writes a new rate, so the session does not go stale. */
export const forgetLabourRate = (): void => { cachedRate = null; };

/**
 * WHAT THIS SHOP WORKS ON — vehicle or door.
 *
 * Swift Garage Door does not have vehicles, and every screen in here said
 * so. The words come from src/domain/trade.ts; this is the one lookup that
 * says which set to use. Cached for the session for the same reason the
 * rate is: it is one short string that changes when a shop changes trade,
 * which is never.
 *
 * RLS means the single row this reads is always the signed-in shop's own,
 * exactly as shopLabourRate() relies on. A missing or failed read is
 * 'auto', which is what every screen assumed before migration 0107.
 */
let cachedTrade: string | null = null;

export async function shopTrade(): Promise<string> {
  if (cachedTrade != null) return cachedTrade;
  try {
    const sb = await db();
    const { data } = await sb.from("accounts").select("trade").limit(1).maybeSingle();
    cachedTrade = (data as { trade: string | null } | null)?.trade ?? "auto";
  } catch {
    cachedTrade = "auto";
  }
  return cachedTrade;
}

/** Called when settings writes a new trade, so the session does not go stale. */
export const forgetTrade = (): void => { cachedTrade = null; };

/**
 * THE SHOP'S OWN TIME ZONE (accounts.timezone), or null.
 *
 * Same shape as shopTrade(). Read for the out-of-area flag on a lead card
 * (src/domain/outofarea.ts). A failed read is null, and null means no
 * flag, never a guessed zone.
 */
let cachedZone: string | null | undefined;

export async function shopTimezone(): Promise<string | null> {
  if (cachedZone !== undefined) return cachedZone;
  try {
    const sb = await db();
    const { data } = await sb.from("accounts").select("timezone").limit(1).maybeSingle();
    cachedZone = (data as { timezone: string | null } | null)?.timezone ?? null;
  } catch {
    cachedZone = null;
  }
  return cachedZone;
}

/**
 * THE SHOP'S EPA IDENTIFICATION NUMBER, or null.
 *
 * Same shape and same reasoning as shopTrade(): one row, read once a
 * session, RLS makes it the signed-in shop's own.
 *
 * A FAILED READ IS NULL, and null means the invoice prints no EPA
 * number, which is exactly what a shop that has not set one wants. So a
 * column that does not exist yet (before 0125) and a shop that has not
 * filled it in behave identically, and neither breaks a screen. Nothing
 * here ever produces a number, which is the point -- see
 * src/domain/hazardous.ts.
 */
let cachedEpa: string | null | undefined;

export async function shopEpaId(): Promise<string | null> {
  if (cachedEpa !== undefined) return cachedEpa;
  try {
    const sb = await db();
    const { data } = await sb.from("accounts").select("epa_id").limit(1).maybeSingle();
    cachedEpa = (data as { epa_id: string | null } | null)?.epa_id ?? null;
  } catch {
    cachedEpa = null;
  }
  return cachedEpa;
}

/** Called when settings writes one, so the session does not go stale. */
export const forgetEpaId = (): void => { cachedEpa = undefined; };

/**
 * CAN THIS SHOP TAKE A CARD?
 *
 * Card charges run on Authorize.net keys that belong to Supercanic, so a
 * card taken by any OTHER shop would bill Supercanic's merchant account --
 * real money into the wrong bank. Until a network shop brings its own
 * merchant, "card" is Supercanic-only, on the desk and on the customer's
 * page alike. This id is Supercanic's account (SUPERCANIC in
 * server/desk/shop.ts); RLS means the one row this reads is always the
 * signed-in shop's own.
 */
const CARD_MERCHANT_ACCOUNT = "7097c673-aa9f-4f0e-ba24-75050a5a29ae";
let cardCap: boolean | null = null;
export async function shopTakesCard(): Promise<boolean> {
  if (cardCap != null) return cardCap;
  const sb = await db();
  const { data } = await sb.from("accounts").select("id").limit(1).maybeSingle();
  cardCap = (data as { id: string } | null)?.id === CARD_MERCHANT_ACCOUNT;
  return cardCap;
}

/**
 * WHOSE REVIEW PAGE THIS SHOP ASKS FOR. The shop's id and its own review
 * link, read once a session like the others. A failed read is null, and
 * reviewForShop() turns null into no link, never into Supercanic's.
 */
let cachedReviewShop: { id: string; review_url: string | null } | null | undefined;

export async function shopReviewLink(): Promise<{ id: string; review_url: string | null } | null> {
  if (cachedReviewShop !== undefined) return cachedReviewShop;
  try {
    const sb = await db();
    let { data, error } = await sb.from("accounts").select("id,review_url").limit(1).maybeSingle();
    // Before migration 0170 the column is not there. Read the id alone, so
    // Supercanic keeps its markets and every other shop still gets none.
    if (error) ({ data, error } = await sb.from("accounts").select("id").limit(1).maybeSingle());
    const row = error ? null : (data as { id: string; review_url?: string | null } | null);
    cachedReviewShop = row ? { id: row.id, review_url: row.review_url ?? null } : null;
  } catch {
    cachedReviewShop = null;
  }
  return cachedReviewShop;
}

export const forgetShopReviewLink = (): void => { cachedReviewShop = undefined; };

/**
 * WHERE THE SHOP IS, for a job that is in it.
 *
 * Anthony: "If the job is marked In the Shop, shops can add their shop
 * address in their shop profile and customers will get the proper address.
 * This never needs to be changed in the area this is in now."
 *
 * One address, written once, in the place a shop already fills in when it
 * signs up. Typing it onto every in-shop job was three chances to get it
 * wrong and no chance to fix it everywhere at once.
 *
 * Cached for the same reason the labor rate is: it changes about never,
 * and a round trip per job card for it would be silly.
 */
let cachedAddress: string | null | undefined;

export async function shopAddress(): Promise<string | null> {
  if (cachedAddress !== undefined) return cachedAddress;
  const sb = await db();
  const { data } = await sb.from("accounts").select("address").limit(1).maybeSingle();
  cachedAddress = (data as { address: string | null } | null)?.address ?? null;
  return cachedAddress;
}

export const forgetShopAddress = (): void => { cachedAddress = undefined; };

export async function updateVehicleField(
  vehicleId: string, column: string, value: string,
): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("vehicles")
    .update({ [column]: value.trim() || null }).eq("id", vehicleId);
  if (error) throw new Error(error.message);
}

/**
 * ONE AIR CONDITIONING READING, WRITTEN OR CLEARED.
 *
 * Its own function rather than a fifth entry in JOB_FIELDS, because
 * updateJobField writes `value.trim() || null` and that is a text rule.
 * On a numeric column it happens to survive "0" today, and it survives
 * it by accident -- one refactor to `Number(value) || null` and every
 * zero reading on the book becomes "not taken". A low side of 0 psi is
 * an empty system, which is a diagnosis. See src/domain/acreadings.ts.
 *
 * Empty clears the reading. Anything that is not a number is refused
 * with a sentence rather than stored as null, because silently dropping
 * a measurement somebody just typed is the worst of the three outcomes.
 */
/**
 * SAVE THE DIAGNOSTIC CARD.
 *
 * Its own function rather than a JOB_FIELDS entry because it is the one
 * field on the job with a trigger behind it (0188 stamps who and when),
 * and because a card that did not save has to say so in words a technician
 * holding a phone can act on. Empty clears the card.
 *
 * The update asks for the row back. An update that matches nothing comes
 * back as success from PostgREST, and a card that silently did not save is
 * the exact failure this screen exists to end.
 */
export type Diagnosis = {
  diagnosis: string | null; diagnosed_at: string | null; diagnosed_by: string | null;
};

export async function saveDiagnosis(id: string, text: string): Promise<Diagnosis> {
  const sb = await db();
  const { data, error } = await sb.from("jobs")
    .update({ diagnosis: text.trim() || null })
    .eq("id", id)
    .select("diagnosis,diagnosed_at,diagnosed_by")
    .maybeSingle();
  if (error) {
    if (isMissingColumn(error)) {
      throw new Error("The diagnostic card is not switched on for this shop yet. "
        + "Put what you found in the job notes for now and tell the office.");
    }
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("That did not save. You may not be on this job any more. "
      + "Reload and try again.");
  }
  return data as Diagnosis;
}

/**
 * SHOW THE CARD TO THE CUSTOMER, OR KEEP IT OFF THEIR PAGE (0189).
 * Read back for the same reason as saveDiagnosis: a switch that did not
 * flip must not look like it did.
 */
export async function setDiagnosisHidden(id: string, hidden: boolean): Promise<boolean> {
  const sb = await db();
  const { data, error } = await sb.from("jobs")
    .update({ diagnosis_hidden: hidden })
    .eq("id", id)
    .select("diagnosis_hidden")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) {
    throw new Error("That did not change. You may not be on this job any more. "
      + "Reload and try again.");
  }
  return Boolean((data as { diagnosis_hidden: boolean }).diagnosis_hidden);
}

const AC_FIELDS = new Set(["ac_high_psi", "ac_low_psi", "ac_vent_f", "ac_ambient_f"]);

export async function setAcReading(id: string, column: string, value: string): Promise<void> {
  if (!AC_FIELDS.has(column)) throw new Error(`${column} is not a reading.`);
  const s = value.trim();
  let v: number | null = null;
  if (s) {
    const n = Number(s);
    if (!Number.isFinite(n)) throw new Error("A reading has to be a number, or empty.");
    v = n;
  }
  const sb = await db();
  const { error } = await sb.from("jobs").update({ [column]: v }).eq("id", id);
  // 23514 is jobs_ac_readings_plausible in 0126, which catches a pressure
  // typed into the temperature box. Said in words.
  if (error) {
    if (error.code === "23514") {
      throw new Error(
        "That reading is outside anything a real system produces. Check the "
        + "box it went in. Pressures are psi, temperatures are Fahrenheit.",
      );
    }
    throw new Error(error.message);
  }
}

export async function updateJobField(id: string, column: string, value: string): Promise<void> {
  if (!JOB_FIELDS.has(column)) throw new Error(`${column} is not a field you can change here.`);
  const sb = await db();
  const { error } = await sb.from("jobs").update({ [column]: value.trim() || null }).eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Where the work happens. Its own function rather than a text field,
 * because it is a choice between two known things and typing "moblie" once
 * is enough to lose a job off a filter for ever.
 */
/**
 * MOVE THE RECORD ALONG, OR BACK.
 *
 * Anthony: "If it's in the job stage, we can click and change it to a lead
 * for example if it cancels. Let us change the stage of the record."
 *
 * Backwards has to be allowed. Work gets cancelled, a customer changes
 * their mind, somebody presses the wrong thing — and a system that only
 * goes forwards makes people lie to it to get where they need to be.
 *
 * The ONE rule that still holds is protocol: nothing reaches in_progress
 * without an authorization on file. That is not about trust, it is what
 * the shop can defend afterwards, so it refuses with a reason rather than
 * hiding the option.
 */
export async function setStatus(
  id: string, status: Status,
  /**
   * `noCharge` is the warranty case, and it answers both of the other two.
   * A job the shop has decided not to bill has no figure for a customer to
   * agree to and no priced line to check, so demanding either is demanding
   * a fiction. The decision itself is the record: who marked it, when, and
   * in what words.
   */
  guard: { hasAuthorization: boolean; hasPricedLine: boolean; noCharge?: boolean },
  /**
   * A reason, when somebody senior is deliberately stepping past a rule.
   * Its presence IS the override — there is no boolean, because a boolean
   * can be set by accident and a sentence cannot.
   */
  override?: string,
  /**
   * `archiving` means this close is a tidy-up of the board, not a decision
   * that the work is off. The status is `cancelled` either way; this is
   * what tells the two apart on the record and in the bell.
   */
  opts?: { archiving?: boolean },
): Promise<void> {
  /**
   * CANCELLING IS THE DESK'S, AND IT IS REFUSED HERE RATHER THAN HIDDEN.
   *
   * Job 1747 was cancelled by the technician it was assigned to, three
   * hours after the customer authorized $450 on his own screen. Hiding the
   * button would have stopped that particular press; it would not stop the
   * next screen that forgets to ask, and there are already two callers
   * (the job card and the jobs list's Archive) plus whatever comes later.
   *
   * So the refusal lives at the one door every one of them goes through.
   * It throws rather than returning quietly, because a cancel that looks
   * like it worked and did not is the silent-failure shape this codebase
   * spends most of its comments avoiding.
   */
  if (status === "cancelled") {
    const sbRole = await db();
    const { data: who } = await sbRole.auth.getUser();
    const { data: mine } = await sbRole.from("profiles")
      .select("role").eq("id", who.user?.id ?? "").maybeSingle();
    const role = (mine as { role: Role } | null)?.role;
    // An unreadable role is not permission. The desk can always try again;
    // a wrongly-allowed cancel cannot be un-agreed with the customer.
    const verdict = mayCancel(role ?? "technician");
    if (!verdict.allowed) throw new Error(verdict.because);
  }

  const blocked: string[] = [];
  const free = Boolean(guard.noCharge);
  if (status === "in_progress" && !guard.hasAuthorization && !free) {
    blocked.push("No authorization on this job — signed or spoken, it has to exist before the work does.");
  }
  if (status === "scheduled") {
    const sb0 = await db();
    const { data } = await sb0.from("jobs").select("scheduled_at").eq("id", id).maybeSingle();
    if (!(data as { scheduled_at: string | null } | null)?.scheduled_at) {
      blocked.push("Booked needs a date, or it sits on no board.");
    }
  }
  if (["completed", "invoiced", "paid"].includes(status) && !guard.hasAuthorization && !free) {
    blocked.push("Finishing a job nobody authorized.");
  }

  /**
   * BILLED MEANS THERE IS A BILL.
   *
   * The stage chips let anybody set a job to Billed or Paid directly, and
   * nothing checked that an invoice existed. Ludmila Vonseca's pre-purchase
   * inspection sat at "invoiced" with no invoice behind it: she had paid,
   * the shop thought it was billed, the Owed column showed nothing, and
   * she had no receipt to be sent. The status was a claim with nothing
   * under it.
   *
   * Raising the invoice is one tap on the same card, so this is a nudge
   * rather than a wall — and like every other rule here, somebody senior
   * can step past it with a reason on the record.
   */
  /* A no-charge job never reaches invoiced or paid: there is no bill, so
     `completed` is where it stops. The rule below therefore still holds for
     everything that does get billed. */
  if (["invoiced", "paid"].includes(status) && !free) {
    const sb2 = await db();
    const { data: inv } = await sb2.from("invoices").select("id").eq("job_id", id).limit(1);
    if (!((inv ?? []) as unknown[]).length) {
      blocked.push("No invoice on this job yet — raise it first, or the customer has no bill to pay and nothing to keep.");
    }
  }

  /**
   * NO JOB CLOSES ON AN UNIDENTIFIED CAR.
   *
   * Anthony: "We should also be still collecting either plate/vin on every
   * job before it closes but trying to get it from the beginning."
   *
   * Asked for at the start and REQUIRED at the end, which is the right way
   * round: at intake it is one more question on a call the shop is trying
   * to keep short, and by the time the work is done somebody has stood next
   * to the car and can simply read it off.
   *
   * Either will do. A plate is what the customer knows and what the shop
   * writes on a board; a VIN is what a parts counter, a warranty claim and
   * a service history actually need. Demanding both at the counter would
   * get one of them invented.
   *
   * A job with no identifiable car is a job that cannot be warrantied,
   * cannot be looked up in three months, and cannot answer "did you work
   * on this vehicle" — which is the question that arrives with a lawyer.
   */
  if (["completed", "invoiced", "paid"].includes(status)) {
    const sb1 = await db();
    const { data: j } = await sb1.from("jobs")
      .select("vehicle_id,vehicles(license_plate,vin,make,model,description)")
      .eq("id", id).maybeSingle();
    const v = (j as { vehicles: Identifiable } | null)?.vehicles;
    /* What counts as identified depends on the trade. A door has no
       plate and may have no readable serial, but a brand and model or
       a written description says which one it was just as well. */
    const trade = await shopTrade();
    if (!isIdentified(trade, v)) blocked.push(words(trade).identifyBlocked);
  }
  if (blocked.length && !override?.trim()) {
    const e = new Error(blocked.join(" "));
    (e as Error & { protocol?: string[] }).protocol = blocked;
    throw e;
  }
  const sb = await db();

  /**
   * WHAT IT WAS BEFORE, read first, because after the write it is gone.
   *
   * Only an OVERRIDE was ever written to the history. An ordinary status
   * change wrote nothing at all — so on 2 September somebody's finger put
   * Danyil German's job from `paid` back to `cancelled`, a job with 19
   * photos, 9 lines and $646.40 collected on it, and the only trace
   * anywhere in the system was `updated_at`. Not who, not when in a way
   * anybody could read, not what it was before. There was nothing to
   * debug because nothing had been written down.
   *
   * Every move goes on the record now. It is one row and it is the
   * difference between a mistake you can see and a mistake you cannot.
   */
  const { data: before } = await sb.from("jobs")
    .select("status,account_id,completed_at,started_at").eq("id", id).maybeSingle();
  const was = (before as { status: string; account_id: string } | null);

  const patch: Record<string, unknown> = { status };
  /**
   * ARCHIVING SAYS SO IN THE SAME BREATH AS THE STATUS.
   *
   * Both the Archive button and a real cancellation write `cancelled`,
   * because that is the only closed status there is and adding a seventh
   * would turn thirty "is this job closed" checks into thirty questions.
   * So the row carries WHICH of the two it was, and
   * `ring_on_cancelled_authorized_job` reads it to decide whether to ring
   * red or orange.
   *
   * IT HAS TO BE IN THIS PATCH. The trigger fires on this update. Stamping
   * archived_at in a second write afterwards would land after the bell had
   * already rung, which is the same shape of bug as raising a row for an
   * act the deployed page had no button for.
   *
   * And leaving `cancelled` clears it: a job pulled back onto the board is
   * no longer archived, and if it is closed again later that is a fresh
   * decision rather than the old one still standing.
   */
  if (status === "cancelled") {
    if (opts?.archiving) patch["archived_at"] = new Date().toISOString();
  } else {
    patch["archived_at"] = null;
  }
  /**
   * ANY OF THE THREE FINISHED STATES STAMPS THE FINISH.
   *
   * This stamped only `completed`, so a job that went straight from
   * estimate to paid — which is every job somebody settles at the curb —
   * never got a date. Jarrod Smith's $150 was paid, collected, and absent
   * from the railway in every window, because the Done stage counts what
   * got finished in the period and that job had no day to be counted on.
   * 672 jobs in this database were in that state.
   *
   * Never overwritten: a finish time already on the record was put there
   * by whoever did the work and knows better than this line does.
   */
  if (["completed", "invoiced", "paid"].includes(status)
      && !(before as { completed_at?: string | null } | null)?.completed_at) {
    patch["completed_at"] = new Date().toISOString();
  }
  /**
   * AND THE MOVE INTO WORK STAMPS THE START, for the same reason.
   *
   * `started_at` was null on all 1,999 jobs in this database. Nothing had
   * ever written it — not the desk, not the wizard, not the portal — while
   * the railway dates its In progress stage by that column. So a job that
   * got started left Booked, because its status was no longer `scheduled`,
   * and never arrived at In progress, because the stage filters on a date
   * it did not have. It fell off the railway between two tiles, which is
   * what Anthony was looking at: work he knew about, on no stage.
   *
   * The database stamps it too (0085), because a status reaches
   * `in_progress` from more places than this function. This line is here
   * so the screen in front of a person is right without a refetch.
   */
  if (status === "in_progress"
      && !(before as { started_at?: string | null } | null)?.started_at) {
    patch["started_at"] = new Date().toISOString();
  }
  const { error } = await sb.from("jobs").update(patch).eq("id", id);
  if (error) throw new Error(error.message);

  if (was && was.status !== status) {
    const { data: me } = await sb.auth.getUser();
    const { data: prof } = await sb.from("profiles")
      .select("full_name").eq("id", me.user?.id ?? "").maybeSingle();
    const who = (prof as { full_name: string } | null)?.full_name ?? "somebody";
    // A failed note must never undo a status change that already landed.
    const { error: logErr } = await sb.from("job_events").insert({
      account_id: was.account_id, job_id: id,
      kind: "status_change", actor: "staff", actor_id: me.user?.id ?? null,
      title: `${STATUS_LABEL[was.status as Status] ?? was.status} \u2192 ${
        STATUS_LABEL[status] ?? status}`,
      detail: `Moved by ${who}`,
      meta: { from: was.status, to: status },
      visible_to_customer: false,
    });
    if (logErr) console.error("status change not recorded:", logErr.message);
  }

  /**
   * THE REASON GOES ON THE RECORD, not into the ether.
   *
   * THIS USED TO TEST `blocked.length` AS WELL, and that was a hole with a
   * prompt in front of it. Nothing blocks a move to `cancelled`, so the
   * quote screen asked "Why, for the record — went elsewhere, changed
   * their mind, cannot reach them", the advisor typed it, and the answer
   * was thrown away. Same for every job cleared with the Archive button,
   * which passes "Archived from the jobs list" and had it dropped. The
   * status said cancelled and nothing anywhere said why or by whom, which
   * is the exact question asked six weeks later when a customer rings up
   * about a quote that vanished.
   *
   * So any reason that was given is written down. When it also stepped
   * past a protocol rule it says that too, because those are different
   * claims: one is a decision, the other is a decision made over an
   * objection.
   */
  if (override?.trim()) {
    const { data: me } = await sb.auth.getUser();
    const { data: prof } = await sb.from("profiles")
      .select("account_id,full_name").eq("id", me.user?.id ?? "").maybeSingle();
    const p = prof as { account_id: string; full_name: string } | null;
    if (p) {
      const stepped = blocked.length > 0;
      // The status change already happened; a failed note must not undo it.
      const { error: noteErr } = await sb.from("job_events").insert({
        account_id: p.account_id, job_id: id,
        kind: stepped ? "protocol_override" : "status_reason",
        actor: "staff", actor_id: me.user?.id ?? null,
        title: stepped
          ? `Moved to ${STATUS_LABEL[status] ?? status} without the usual checks`
          : `Why it moved to ${STATUS_LABEL[status] ?? status}`,
        detail: `${p.full_name}: ${override.trim()}`,
        meta: { ...(stepped ? { skipped: blocked } : {}), to: status },
        visible_to_customer: false,
      });
      if (noteErr) console.error("reason not recorded:", noteErr.message);
    }
  }
}

/**
 * MARK A JOB AS ONE THE SHOP IS NOT BILLING, or take the mark off.
 *
 * A reason and nothing else: warranty, goodwill, our mistake, five minutes.
 * "no_charge = true" would tell the next person nothing, and those four are
 * different decisions that read the same in a total column at year end.
 */
export async function setNoCharge(id: string, reason: string | null): Promise<void> {
  const sb = await db();
  const why = (reason ?? "").trim();
  const { data: me } = await sb.auth.getUser();

  const { error } = await sb.from("jobs").update(why
    ? { no_charge_reason: why, no_charge_by: me.user?.id ?? null,
        no_charge_at: new Date().toISOString() }
    : { no_charge_reason: null, no_charge_by: null, no_charge_at: null },
  ).eq("id", id);
  if (error) throw new Error(error.message);

  const { data: prof } = await sb.from("profiles")
    .select("account_id,full_name").eq("id", me.user?.id ?? "").maybeSingle();
  const p = prof as { account_id: string; full_name: string } | null;
  if (!p) return;

  // On the job's own history, because "why was this never billed" is asked
  // months later by somebody who was not there.
  const { error: noteErr } = await sb.from("job_events").insert({
    account_id: p.account_id, job_id: id,
    kind: why ? "no_charge" : "no_charge_cleared", actor: "staff",
    actor_id: me.user?.id ?? null,
    title: why ? "Marked as no charge" : "No longer a no-charge job",
    detail: why ? `${p.full_name}: ${why}` : `Cleared by ${p.full_name}`,
    meta: { reason: why || null },
    visible_to_customer: false,
  });
  if (noteErr) console.error("no-charge not recorded:", noteErr.message);
}

/** The appointment, as a wall-clock value the browser gave us. */
export async function setScheduledAt(id: string, localValue: string): Promise<void> {
  const sb = await db();
  // An empty box clears it. That drops the job out of `scheduled`, because
  // the schema will not hold a booking with no date, so it goes back to
  // being the estimate it actually is.
  if (!localValue.trim()) {
    const { error } = await sb.from("jobs")
      .update({ scheduled_at: null, status: "estimate" }).eq("id", id);
    if (error) throw new Error(error.message);
    return;
  }
  // The box is the shop's wall clock, whoever is looking at it.
  const when = fromShopWall(localValue);
  if (!when) throw new Error("That is not a date the browser understands.");
  const { error } = await sb.from("jobs")
    .update({ scheduled_at: when.toISOString(), status: "scheduled" }).eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * MAKE A CAR AND HANG IT ON THIS JOB.
 *
 * A job could be raised with no vehicle — from a phone call, from a lead
 * with nothing but a name — and then there was no way to give it one. The
 * plate and VIN fields on the card edit the vehicle RECORD, so with no
 * record they had nothing to write to.
 *
 * account_id is passed EXPLICITLY. The column defaults to
 * current_account_id(), which reads the caller's session, and the last
 * system orphaned twenty-eight cars that way when a server-side insert
 * had no session for the default to read.
 */
export async function attachVehicle(
  jobId: string,
  car: { year: number | null; make: string | null; model: string | null;
         license_plate: string | null; vin: string | null },
): Promise<string> {
  const sb = await db();
  const { data: j } = await sb.from("jobs")
    .select("account_id,customer_id").eq("id", jobId).maybeSingle();
  const on = j as { account_id: string; customer_id: string | null } | null;
  if (!on) throw new Error("That job is not on file.");

  const { data, error } = await sb.from("vehicles").insert({
    account_id: on.account_id, customer_id: on.customer_id,
    year: car.year, make: car.make, model: car.model,
    license_plate: car.license_plate, vin: car.vin,
  }).select("id").single();
  if (error) throw new Error(carSaveRefusal("Could not save the car", error));

  const vehicleId = (data as { id: string }).id;
  const { error: linkErr } = await sb.from("jobs")
    .update({ vehicle_id: vehicleId }).eq("id", jobId);
  if (linkErr) throw new Error(`The car saved but did not attach: ${linkErr.message}`);
  return vehicleId;
}

/**
 * WHO IS ON THIS JOB.
 *
 * `job_technicians` has existed since the first migration and nothing in
 * the interface ever wrote to it, so every job in here shows no one
 * working it. A shop with three vans cannot answer "who has the Tundra"
 * from a screen that does not ask.
 *
 * A job can have more than one person on it — two on a transmission is
 * normal — and exactly one of them is the lead, because somebody has to be
 * the one the desk rings.
 */
export type OnJob = { technician_id: string; is_lead: boolean };

export async function jobTechnicians(jobId: string): Promise<OnJob[]> {
  const sb = await db();
  const { data } = await sb.from("job_technicians")
    .select("technician_id,is_lead").eq("job_id", jobId);
  return (data ?? []) as OnJob[];
}

/**
 * Replace the whole set. Delete then insert, in that order, so a person
 * removed and re-added in one edit cannot collide with themselves on the
 * primary key.
 */
export async function setJobTechnicians(
  jobId: string, ids: string[], leadId: string | null,
): Promise<void> {
  const sb = await db();
  const { error: gone } = await sb.from("job_technicians").delete().eq("job_id", jobId);
  if (gone) throw new Error(gone.message);
  if (!ids.length) return;

  const { error } = await sb.from("job_technicians").insert(
    ids.map((technician_id) => ({
      job_id: jobId, technician_id,
      is_lead: technician_id === (leadId ?? ids[0]),
    })));
  if (error) throw new Error(error.message);
}

/**
 * PUT ME ON THIS JOB.
 *
 * Anthony: "Matt Bolen is both owner and operator, needs to be able to be
 * service advisor and tech."
 *
 * The shop's roles were built for a shop with a desk and a yard: an
 * advisor prices the work, a technician does it, and `technicians` is a
 * different table from `profiles` because in that shop they are different
 * people. Matt is one person. He signed up as the owner, which made him a
 * profile and not a technician, so there was nobody the system would let
 * him put on his own jobs. "Who is on it" said Nobody yet on every job he
 * has ever done, and his own work is attributed to no one.
 *
 * This is the one-person case made ordinary: a profile can become a
 * technician record without an administrator doing anything, linked by
 * `profile_id` so payroll, the day board and the crew bar all recognize
 * them as the same human afterwards.
 *
 * It is deliberately idempotent. Pressing it twice is what somebody does
 * when they are not sure it worked the first time.
 */
export async function meAsTechnician(): Promise<string> {
  const sb = await db();
  const { data: auth } = await sb.auth.getUser();
  const uid = auth.user?.id ?? "";
  if (!uid) throw new Error("Sign in first.");

  const { data: mine } = await sb.from("technicians")
    .select("id").eq("profile_id", uid).maybeSingle();
  if (mine) return (mine as { id: string }).id;

  const { data: prof } = await sb.from("profiles")
    .select("account_id,full_name,phone").eq("id", uid).maybeSingle();
  const p = prof as { account_id: string; full_name: string; phone: string | null } | null;
  if (!p) throw new Error("Your login is not attached to a shop.");

  /* A technician with the same name may already exist from an import, in
     which case the right answer is to claim it rather than to make a
     second one and split the person's history in half. */
  const { data: sameName } = await sb.from("technicians")
    .select("id,profile_id").eq("account_id", p.account_id).eq("name", p.full_name).maybeSingle();
  const found = sameName as { id: string; profile_id: string | null } | null;
  if (found && !found.profile_id) {
    const { error } = await sb.from("technicians")
      .update({ profile_id: uid, active: true }).eq("id", found.id);
    if (error) throw new Error(error.message);
    return found.id;
  }
  if (found) return found.id;

  const { data: made, error } = await sb.from("technicians").insert({
    account_id: p.account_id, profile_id: uid,
    name: p.full_name, phone: p.phone, active: true,
  }).select("id").single();
  if (error) throw new Error(error.message);
  return (made as { id: string }).id;
}

/** Am I already a technician record. Null when I am not. */
export async function myTechnicianId(): Promise<string | null> {
  const sb = await db();
  const { data: auth } = await sb.auth.getUser();
  const { data } = await sb.from("technicians")
    .select("id").eq("profile_id", auth.user?.id ?? "").maybeSingle();
  return (data as { id: string } | null)?.id ?? null;
}

/**
 * A CUSTOMER'S PORTAL TOKEN, made if it is not there.
 *
 * The token is how `/api/pay` and the garage page know who somebody is,
 * so a customer without one cannot be sent a quote, cannot be sent an
 * invoice and cannot be charged at the counter. Thirty-one of them had no
 * token, and the answer the screen gave was "open their record and save it
 * once", which is a chore invented by the software.
 *
 * Twenty-four random bytes, the same shape mint_portal_token() makes,
 * generated in the browser because that is where the button was pressed.
 * It is a bearer link and nothing else: it grants exactly what the garage
 * page shows and it is per customer, so a leaked one is one customer's own
 * job history.
 */
export async function ensurePortalToken(customerId: string): Promise<string | null> {
  const sb = await db();
  const { data } = await sb.from("customers")
    .select("portal_token").eq("id", customerId).maybeSingle();
  const have = (data as { portal_token: string | null } | null)?.portal_token;
  if (have) return have;

  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const token = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

  // Read it back. A PostgREST update that matches nothing answers 200 with
  // an empty list, so "no error" is not the same as "it saved".
  const { data: saved, error } = await sb.from("customers")
    .update({ portal_token: token }).eq("id", customerId).select("portal_token");
  if (error || !(saved ?? []).length) return null;
  return token;
}

export async function setServiceAt(id: string, at: "mobile" | "shop" | null): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("jobs").update({ service_at: at }).eq("id", id);
  if (error) {
    throw new Error(isMissingColumn(error)
      ? "Mobile or shop needs migration 0003 — run db/0002-paste-me.sql first."
      : error.message);
  }
}
